import React, { useState, useEffect } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import EmploymentStatusBadge from "./EmploymentStatusBadge";

// Iter60.p59 — "Suggest Undeployed" review modal.
//
// Fetches `GET /api/employees/undeployed-candidates` on open. User
// picks which candidates to flip to `undeployed` via checkboxes, then
// clicks "Mark selected as Undeployed" → single POST /status/bulk
// call.
//
// Props:
//   open:         boolean
//   onOpenChange: (bool) => void
//   moduleId:     optional filter (Manager scope inference)
//   moduleCode:   optional filter (falls back to path scope)
//   onSaved:      () => void
export default function UndeployedCandidatesDialog({
    open,
    onOpenChange,
    moduleId,
    moduleCode,
    onSaved,
}) {
    const [candidates, setCandidates] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            setLoading(true);
            try {
                const params = {};
                if (moduleId) params.module_id = moduleId;
                if (moduleCode) params.module_code = moduleCode;
                const { data } = await api.get("/employees/undeployed-candidates", { params });
                setCandidates(data);
                setSelected(new Set(data.map((c) => c.id)));  // default: all pre-checked
            } catch (e) {
                toast.error(formatApiError(e));
                setCandidates([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [open, moduleId, moduleCode]);

    const toggle = (id) => {
        const s = new Set(selected);
        if (s.has(id)) s.delete(id); else s.add(id);
        setSelected(s);
    };
    const toggleAll = () => {
        setSelected(selected.size === candidates.length ? new Set() : new Set(candidates.map((c) => c.id)));
    };

    const submit = async () => {
        if (selected.size === 0) return;
        setBusy(true);
        try {
            const { data } = await api.post("/employees/status/bulk", {
                employee_ids: [...selected],
                status: "undeployed",
                note: "Suggested via 'Suggest Undeployed' review — never used biometric machine.",
            });
            toast.success(`Marked ${data.updated} as Undeployed.`);
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl" data-testid="undeployed-dialog">
                <DialogHeader>
                    <DialogTitle data-testid="undeployed-title">
                        Suggest Undeployed
                    </DialogTitle>
                    <DialogDescription>
                        Active employees who have <span className="font-semibold">never clocked in</span>.
                        Pick the workers you want to mark as <EmploymentStatusBadge status="undeployed" size="xs" testid="undeployed-preview-chip" />.
                    </DialogDescription>
                </DialogHeader>

                <div className="max-h-[420px] overflow-y-auto rounded border" style={{ borderColor: "var(--asl-border)" }}>
                    {loading ? (
                        <div className="p-6 text-center text-sm text-[color:var(--asl-muted)]" data-testid="undeployed-loading">
                            Loading candidates…
                        </div>
                    ) : candidates.length === 0 ? (
                        <div className="p-6 text-center text-sm text-[color:var(--asl-muted)]" data-testid="undeployed-empty">
                            No active workers with zero lifetime biometric events. You&#39;re all caught up.
                        </div>
                    ) : (
                        <table className="asl-table asl-table--zebra w-full" data-testid="undeployed-table">
                            <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                   style={{ background: "var(--asl-navy-50)", borderBottom: "1px solid var(--asl-border)" }}>
                                <tr>
                                    <th className="px-3 py-2 w-8">
                                        <Checkbox
                                            checked={selected.size === candidates.length && candidates.length > 0}
                                            onCheckedChange={toggleAll}
                                            aria-label="Select all candidates"
                                            data-testid="undeployed-select-all"
                                        />
                                    </th>
                                    <th className="text-left px-3 py-2">Module ID</th>
                                    <th className="text-left px-3 py-2">Name</th>
                                    <th className="text-left px-3 py-2">Job Title</th>
                                    <th className="text-left px-3 py-2">Hire Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {candidates.map((c) => (
                                    <tr key={c.id} data-testid={`undeployed-row-${c.id}`}>
                                        <td className="px-3 py-2">
                                            <Checkbox
                                                checked={selected.has(c.id)}
                                                onCheckedChange={() => toggle(c.id)}
                                                aria-label={`Select ${c.full_name}`}
                                                data-testid={`undeployed-check-${c.id}`}
                                            />
                                        </td>
                                        <td className="px-3 py-2 mono text-xs">
                                            {c.module_employee_id || "—"}
                                        </td>
                                        <td className="px-3 py-2 font-medium asl-navy-text">
                                            {c.full_name}
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            {c.job_title || "—"}
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            {c.hire_date || "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <DialogFooter>
                    <div className="flex-1 text-xs text-[color:var(--asl-muted)]" data-testid="undeployed-summary">
                        {candidates.length > 0 && (
                            <>{selected.size} of {candidates.length} selected</>
                        )}
                    </div>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}
                            data-testid="undeployed-cancel">
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={busy || selected.size === 0}
                        className="asl-navy-bg text-white"
                        data-testid="undeployed-submit"
                    >
                        {busy ? "Saving…" : `Mark ${selected.size} as Undeployed`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
